import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Store, BadgeCheck, GraduationCap, Info, Layers, PlayCircle, Award, Globe, BarChart3, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD, MKT_TINT, TIPOS_LECCION } from "../../lib/marketplace";
import { apiPublicCurso, apiCheckout } from "../../lib/marketplaceApi";
import { CatalogCard, catLabel, precioTxt } from "./publicUi";
import { FavoriteButton } from "../../components/FavoriteButton";
import { COURSE_NOTICE } from "./mktui";
import Seo from "../../components/Seo";
import { humanizeAxiosError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

const tipoLabel = (id) => TIPOS_LECCION.find((t) => t.id === id)?.label || id;

export default function CursoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add, refresh } = useCart();
  const [data, setData] = useState(undefined);
  const [agregando, setAgregando] = useState(false);

  useEffect(() => { apiPublicCurso(id).then(setData).catch(() => setData(null)); }, [id]);

  // "Inscribirme ahora": agrega el curso y va DIRECTO al pago (sin pasar por el carrito).
  const inscribirme = async () => {
    if (!user || user === false) { toast.info("Inicia sesión para inscribirte."); navigate("/login"); return; }
    setAgregando(true);
    try {
      await add("curso", id);
      const r = await apiCheckout(window.location.origin, true, "2026-07");
      if (r.url) { window.location.href = r.url; return; }
      if (r.gratis) {
        await refresh();
        toast.success("¡Listo! Ya estás inscrito. Encuéntralo en Mis Cursos.");
        navigate(`/tienda/pago/exito?order_id=${r.order_id}`);
        return;
      }
      navigate("/tienda/carrito");
    } catch (e) { toast.error(humanizeAxiosError(e)); setAgregando(false); }
  };

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center gap-3"><p className="text-slate-500">Contenido no disponible.</p><Link to="/tienda" className="font-bold" style={{ color: MKT_GOLD }}>Volver al Marketplace</Link></div>;

  const c = data.item;
  const t = c.tienda || {};
  const totalLecc = (c.modulos || []).reduce((n, m) => n + (m.lecciones?.length || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="curso-detalle">
      <Seo title={c.titulo} description={c.descCorta || c.subtitulo || c.descCompleta} image={c.cover} type="product" />
      <header className="text-white" style={{ background: `linear-gradient(135deg, #4C1D95, #2E1065)` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button onClick={() => navigate("/tienda")} className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white"><ChevronLeft size={16} /> Volver al catálogo</button>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 items-start">
            <div>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-white/15"><GraduationCap size={12} /> Curso independiente</span>
              <h1 className="mt-3 text-3xl md:text-4xl font-extrabold flex items-start justify-between gap-3">
                <span>{c.titulo}</span>
                <FavoriteButton item={{ tipo: "curso", ref_id: id, titulo: c.titulo, subtitulo: (t.vendedor || c.instructor || ""), enlace: `/tienda/curso/${id}`, imagen: c.cover || "" }} size={24} className="w-10 h-10 bg-white/15 hover:bg-white/25 flex-shrink-0" />
              </h1>
              {c.subtitulo && <p className="mt-2 text-white/80 text-lg">{c.subtitulo}</p>}
              <Link to={`/tienda/t/${t.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white"><Store size={15} /> {t.vendedor || c.instructor} {t.verificado && <BadgeCheck size={14} className="text-emerald-300" />}</Link>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/80">
                <span className="inline-flex items-center gap-1.5"><BarChart3 size={15} /> {c.nivel || "—"}</span>
                <span className="inline-flex items-center gap-1.5"><Globe size={15} /> {c.idioma || "—"}</span>
                <span className="inline-flex items-center gap-1.5"><Layers size={15} /> {(c.modulos || []).length} módulos</span>
                <span className="inline-flex items-center gap-1.5"><PlayCircle size={15} /> {totalLecc} lecciones</span>
                {c.certificado && <span className="inline-flex items-center gap-1.5"><Award size={15} /> Certificado del instructor</span>}
              </div>
            </div>
            <div className="rounded-2xl bg-white p-5 text-slate-900 shadow-xl">
              <div className="rounded-xl overflow-hidden aspect-video bg-slate-100 flex items-center justify-center">
                {c.cover ? <img src={c.cover} alt={c.titulo} className="w-full h-full object-cover" /> : <GraduationCap size={40} style={{ color: MKT_GOLD }} />}
              </div>
              <p className="mt-4 text-3xl font-extrabold">{precioTxt(c)}</p>
              <button onClick={inscribirme} disabled={agregando} data-testid="btn-inscribirme" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all disabled:opacity-60" style={{ backgroundColor: MKT_GOLD }}>{agregando ? <Loader2 size={18} className="animate-spin" /> : <GraduationCap size={18} />} Inscribirme ahora</button>
              <p className="mt-3 text-[11px] text-slate-400 flex items-start gap-1"><Info size={12} className="mt-0.5" /> {COURSE_NOTICE}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {c.descCompleta && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-2">Acerca de este curso</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line">{c.descCompleta}</p>
          </section>
        )}

        <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="curso-temario">
          <h2 className="font-bold text-slate-900 mb-3">Contenido del curso <span className="text-sm font-normal text-slate-400">(vista previa)</span></h2>
          <div className="space-y-3">
            {(c.modulos || []).map((m, mi) => (
              <div key={mi} className="rounded-xl border border-slate-200">
                <div className="px-4 py-2.5 bg-slate-50 rounded-t-xl font-semibold text-slate-800 text-sm">M{mi + 1}. {m.titulo} <span className="text-slate-400 font-normal">· {m.lecciones?.length || 0} lecciones</span></div>
                <ul className="divide-y divide-slate-50">
                  {(m.lecciones || []).map((l, li) => (
                    <li key={li} className="px-4 py-2.5 flex items-center justify-between text-sm">
                      <span className="inline-flex items-center gap-2 text-slate-600"><PlayCircle size={14} className="text-slate-400" /> {l.titulo || "Lección"} <span className="text-[10px] text-slate-400 px-1.5 py-0.5 rounded bg-slate-100">{tipoLabel(l.tipo)}</span></span>
                      {l.duracion && <span className="text-xs text-slate-400">{l.duracion}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">La reproducción de lecciones estará disponible tras la inscripción.</p>
        </section>

        {c.materiales?.length > 0 && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-bold text-slate-900 mb-3">Materiales incluidos</h2>
            <ul className="space-y-1.5">
              {c.materiales.map((m, i) => <li key={i} className="text-sm text-slate-600 inline-flex items-center gap-2"><Paperclip size={14} style={{ color: MKT_GOLD }} /> {m}</li>)}
            </ul>
          </section>
        )}

        {data.relacionados?.length > 0 && (
          <section data-testid="curso-relacionados">
            <h2 className="text-lg font-extrabold text-slate-900 mb-3">Cursos relacionados</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.relacionados.map((i) => <CatalogCard key={i.id} item={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
